import { useEffect, useState } from 'react';
import type { HighlightRect } from '../HtmlViewerTypes';

interface VisibleRange {
  top: number;
  bottom: number;
}

const FULL_RANGE: VisibleRange = { top: -Infinity, bottom: Infinity };

function findScrollParent(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;
  while (current) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight;
    if (canScroll) return current;
    current = current.parentElement;
  }
  return null;
}

/**
 * Tracks the visible Y range of a container and returns only the
 * HighlightRects that overlap with the visible area.
 *
 * Falls back to returning all rects when viewport data is unavailable.
 */
export function useIntersectionVisibility(
  rects: HighlightRect[],
  geometryRootRef: React.RefObject<HTMLDivElement>
): HighlightRect[] {
  const [visibleRange, setVisibleRange] = useState<VisibleRange>(FULL_RANGE);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const geometryRoot = geometryRootRef.current;
    if (!geometryRoot) return undefined;

    const updateVisibleRange = () => {
      const rootBounds = geometryRoot.getBoundingClientRect();
      const viewportTop = 0;
      const viewportBottom = window.innerHeight;

      const top = Math.max(viewportTop, rootBounds.top) - rootBounds.top;
      const bottom =
        Math.min(viewportBottom, rootBounds.bottom) - rootBounds.top;

      setVisibleRange({ top, bottom });
    };

    updateVisibleRange();

    const scrollParent = findScrollParent(geometryRoot);
    const scrollTarget: EventTarget = scrollParent ?? window;

    scrollTarget.addEventListener('scroll', updateVisibleRange, {
      passive: true,
    });
    window.addEventListener('resize', updateVisibleRange, { passive: true });

    return () => {
      scrollTarget.removeEventListener('scroll', updateVisibleRange);
      window.removeEventListener('resize', updateVisibleRange);
    };
  }, [geometryRootRef]);

  if (visibleRange === FULL_RANGE) return rects;

  return rects.filter(
    rect =>
      rect.y + rect.height >= visibleRange.top &&
      rect.y <= visibleRange.bottom
  );
}
